import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { TranslateService } from '@ngx-translate/core';

import {
  SkillAvailableCurator,
  SkillGroup,
  SkillSpecializationCreateRequest,
  SpecializationFormData,
  SpecializationFormInitialValue,
  SpecializationFormOptions,
  SpecializationFormValue,
} from '../../../models/skill-learning.model';
import { SkillSpecializationFacadeService } from '../../../services/skill-specialization-facade.service';
import { SkillRoleService } from '../../../services/skill-role.service';
import { ToastService } from '../../../../../../services/toast.service';
import { SidebarService } from '../../../../../../services/side-bar.service';
import { UserService } from '../../../../../../services/user.service';
import {
  SKILL_LEARNING_FIRST_INDEX,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_LEARNING_ROUTE,
  SKILL_LEARNING_SIDEBAR,
} from '../../../options/skill-learning.option';
import {
  SPECIALIZATION_CURATOR_SELECTION,
  DEFAULT_SPECIALIZATION_FORM_DATA,
  DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE,
  DEFAULT_SPECIALIZATION_FORM_OPTIONS,
  SPECIALIZATION_FORM_MESSAGE,
  SPECIALIZATION_FORM_SELECTION,
} from '../specialization-form/specialization-form.option';

@Component({
  selector: 'app-create-specialization-container',
  templateUrl: './create-specialization-container.component.html',
  styleUrls: ['./create-specialization-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateSpecializationContainerComponent implements OnInit, OnDestroy {

  public availableCurators: SkillAvailableCurator[] = [];
  public availableFillAssignees: SkillAvailableCurator[] = [];
  public availableGroups: SkillGroup[] = [];
  public selectedGroupId = SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
  public isSubmitting = false;
  public groupsLoadFailed = false;
  public groupUsersLoadFailed = false;
  public groupUsersLoading = false;
  public canCreateCrossDepartmental = false;
  public formInitialValue: SpecializationFormInitialValue = DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE;
  public formData: SpecializationFormData = DEFAULT_SPECIALIZATION_FORM_DATA;
  public formOptions: SpecializationFormOptions = DEFAULT_SPECIALIZATION_FORM_OPTIONS;

  private readonly destroy$ = new Subject<void>();
  private groupUsersRequestId = SKILL_LEARNING_FIRST_INDEX;

  constructor(
    private facade: SkillSpecializationFacadeService,
    private roleService: SkillRoleService,
    private userService: UserService,
    private toastService: ToastService,
    private sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  public get requireCuratorForCurrentUser(): boolean {
    return false;
  }

  public get requireFillAssignee(): boolean {
    return true;
  }

  public get canAssignAnyCuratorLevel(): boolean {
    return this.roleService.isHeadOrAdmin() || !!this.selectedGroupId
      && this.availableGroups.some(group => group.id === this.selectedGroupId);
  }

  public ngOnInit(): void {
    this.sidebarService.setSelectedMenuOption(SKILL_LEARNING_SIDEBAR.SPECIALIZATIONS);
    this.refreshFormConfiguration();
    this.loadCreationPermissions();
    this.loadGroups();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onFormSubmitted(value: SpecializationFormValue): void {
    this.isSubmitting = true;
    this.refreshFormConfiguration();
    const request = this.buildCreateRequest(value);
    this.facade.createSpecialization(request, value.crossDepartmental ? null : value.curators)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleCreateSuccess(),
        () => this.handleCreateError(),
      );
  }

  public onCancelRequested(): void {
    this.router.navigate([SKILL_LEARNING_ROUTE.SPECIALIZATIONS]);
  }

  public onGroupChanged(groupId: string): void {
    this.selectedGroupId = groupId;
    this.loadAvailableCurators(groupId);
  }

  public retryGroups(): void { if (!this.isSubmitting) { this.loadGroups(); } }

  public retryGroupUsers(): void {
    if (!this.isSubmitting && !this.groupUsersLoading) { this.loadAvailableCurators(this.selectedGroupId); }
  }

  private buildCreateRequest(value: SpecializationFormValue): SkillSpecializationCreateRequest {
    const request: SkillSpecializationCreateRequest = {
      name: value.name,
      description: value.description,
      configuredLevelCount: SKILL_LEARNING_LEVEL_COUNT.ONE,
      crossDepartmental: value.crossDepartmental,
      participantGroupIds: value.participantGroupIds,
      responsibleGroupByDepartmentId: value.responsibleGroupByDepartmentId,
    };
    if (!value.crossDepartmental) {
      request.groupId = value.groupId;
      request.fillAssigneeId = value.fillAssigneeId;
    }
    return request;
  }

  private loadGroups(): void {
    this.groupsLoadFailed = false;
    this.facade.fetchGroups()
      .takeUntil(this.destroy$)
      .subscribe(
        groups => {
          this.availableGroups = groups || [];
          this.selectedGroupId = this.availableGroups.length === SPECIALIZATION_CURATOR_SELECTION.SINGLE_GROUP_COUNT
            ? this.availableGroups[SKILL_LEARNING_FIRST_INDEX].id
            : SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE;
          this.refreshFormConfiguration();
          this.loadAvailableCurators(this.selectedGroupId);
          this.cdr.markForCheck();
        },
        () => { this.groupsLoadFailed = true; this.cdr.markForCheck(); },
      );
  }

  private loadCreationPermissions(): void {
    this.facade.fetchCreationPermissions()
      .takeUntil(this.destroy$)
      .subscribe(
        permissions => {
          this.canCreateCrossDepartmental = !!permissions
            && !!permissions.canCreateCrossDepartmental;
          this.refreshFormConfiguration();
          this.cdr.markForCheck();
        },
        () => {
          this.canCreateCrossDepartmental = false;
          this.refreshFormConfiguration();
          this.cdr.markForCheck();
        },
      );
  }

  private loadAvailableCurators(groupId: string): void {
    const requestId = ++this.groupUsersRequestId;
    this.groupUsersLoadFailed = false;
    this.groupUsersLoading = !!groupId;
    this.availableCurators = [];
    this.availableFillAssignees = [];
    this.refreshFormConfiguration();
    if (!groupId) {
      this.cdr.markForCheck();
      return;
    }
    this.loadGroupUsers(groupId, this.canAssignAnyCuratorLevel, requestId);
  }

  private loadGroupUsers(groupId: string, useAsCurators: boolean, requestId: number): void {
    this.facade.fetchGroupUsers(groupId)
      .takeUntil(this.destroy$)
      .subscribe(
        users => this.handleGroupUsersLoaded(users, useAsCurators, requestId),
        () => this.handleGroupUsersError(requestId),
      );
  }

  private handleGroupUsersError(requestId: number): void {
    if (requestId !== this.groupUsersRequestId) { return; }
    this.groupUsersLoading = false;
    this.groupUsersLoadFailed = true;
    this.availableCurators = [];
    this.availableFillAssignees = [];
    this.refreshFormConfiguration();
    this.cdr.markForCheck();
  }

  private handleGroupUsersLoaded(
    users: SkillAvailableCurator[], useAsCurators: boolean, requestId: number,
  ): void {
    if (requestId !== this.groupUsersRequestId) {
      return;
    }
    const groupUsers = users || [];
    this.groupUsersLoading = false;
    this.groupUsersLoadFailed = false;
    this.availableFillAssignees = groupUsers;
    if (useAsCurators) {
      this.availableCurators = groupUsers;
    }
    this.refreshFormConfiguration();
    this.cdr.markForCheck();
  }

  private handleCreateSuccess(): void {
    this.isSubmitting = false;
    this.refreshFormConfiguration();
    this.toastService.success(this.translate.instant('SKILL_LEARNING_ADMIN_CREATE_SUCCESS'));
    this.router.navigate([SKILL_LEARNING_ROUTE.SPECIALIZATIONS]);
  }

  private handleCreateError(): void {
    this.isSubmitting = false;
    this.refreshFormConfiguration();
    this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_CREATE_ERROR'));
    this.cdr.markForCheck();
  }

  private refreshFormConfiguration(): void {
    this.formInitialValue = this.buildFormInitialValue();
    this.formData = this.buildFormData();
    this.formOptions = this.buildFormOptions();
  }

  private buildFormInitialValue(): SpecializationFormInitialValue {
    return Object.assign({}, DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE, {
      groupId: this.selectedGroupId,
    });
  }

  private buildFormData(): SpecializationFormData {
    return {
      manageableCuratorGroupIds: [],
      availableGroups: this.availableGroups,
      availableCurators: this.availableCurators,
      availableFillAssignees: this.availableFillAssignees,
    };
  }

  private buildFormOptions(): SpecializationFormOptions {
    return Object.assign({}, DEFAULT_SPECIALIZATION_FORM_OPTIONS, {
      showGroupSelector: true,
      showCurators: true,
      showFillAssigneeSelector: true,
      showCrossDepartmentalOption: this.canCreateCrossDepartmental,
      isSubmitting: this.isSubmitting,
      submitLabel: SPECIALIZATION_FORM_MESSAGE.CREATE_SUBMIT_LABEL,
      requireCurator: this.requireCuratorForCurrentUser,
      requireFillAssignee: this.requireFillAssignee,
      showFillAssigneeHint: true,
      canAssignAnyCuratorLevel: this.canAssignAnyCuratorLevel,
    });
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/create-specialization-container/create-specialization-container.component.ts